import { Divider } from '@design-system';
import Fuse from 'fuse.js';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { GLOSSARY_INDEX } from '../../constants';
import { GlossaryIndex, GlossaryItem } from '../../types/Glossary';
import { TrackPageView } from '../../utils/analytics';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import PageHeading from '../Layout/PageHeading';
import PageSection from '../Layout/PageSection/PageSection';
import { LinkCardGroup } from '../LinkCards';
import { SearchBar } from '../Search/SearchBar';
import { SupportFAQ } from '../Support/SupportFAQ/SupportFAQ';
import { GlossaryGrid } from './GlossaryGrid';
import { GlossaryList } from './GlossaryList';

interface Props {
  glossary: GlossaryItem[];
  locale: string;
  page: GlossaryIndex;
}

export const GlossaryIndexPage: FC<Props> = ({ glossary, locale, page }) => {
  const { t } = useTranslation('glossary');

  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState(glossary);

  const properties = useMemo(
    () => ({
      glossary_terms_count: glossary.length,
      locale,
    }),
    [glossary.length, locale],
  );

  const [analyticsEmitter] = useAnalyticsEmitter(properties);

  useEffect(() => {
    TrackPageView({
      page: GLOSSARY_INDEX,
      properties,
    });
  }, [properties]);

  const fuse = useMemo(
    () =>
      new Fuse(glossary, {
        includeScore: false,
        keys: ['title'],
        shouldSort: true,
      }),
    [glossary],
  );

  const handleQuery = useCallback(
    (value: string) => {
      const entries = fuse.search(value).map(({ item }) => item);
      setFiltered(entries);
      setQuery(value);
      analyticsEmitter('glossary', 'index search', {
        query: value,
        no_results: entries.length === 0,
        results_count: entries.length,
      });
    },
    [analyticsEmitter, fuse],
  );

  return (
    <main className="my-9 space-y-9 md:my-12 md:space-y-12">
      <div className="relative space-y-3">
        <PageSection>
          <div className="flex w-full flex-1 flex-col space-y-6">
            <PageHeading
              heading={page.content.title}
              strapline={page.content.description}
            />
            <div className="flex w-full flex-1 space-x-3">
              <div className="flex-1">
                <SearchBar
                  onChange={handleQuery}
                  placeholder={t('searchPlaceholder')}
                />
              </div>
            </div>
          </div>
        </PageSection>
      </div>

      <PageSection>
        {query.trim() ? (
          <GlossaryList
            emptyMessage={t('searchEmptyState', { query })}
            glossary={filtered}
            handleClick={(term) => {
              analyticsEmitter('glossary', 'search term click', {
                term: term.title,
              });
            }}
            query={query.trim()}
          />
        ) : (
          <GlossaryGrid
            glossary={glossary}
            handleClick={(term) => {
              analyticsEmitter('glossary', 'list term click', {
                term: term.title,
              });
            }}
          />
        )}
      </PageSection>

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      {page.content.linkCardGroup?.[0] ? (
        <PageSection>
          <LinkCardGroup
            key={page.content.linkCardGroup[0]._uid}
            analyticsPlacement={
              page.content.linkCardGroup[0].analyticsPlacement
            }
            cards={page.content.linkCardGroup[0].cards}
          />
        </PageSection>
      ) : null}

      <div className="wrapper-website">
        <Divider className="my-9 md:my-12" />
      </div>

      <PageSection>
        <SupportFAQ
          analytics={{
            page: GLOSSARY_INDEX,
            context: 'FAQ',
          }}
          faq={page.content.faqContent?.story?.content}
          image={page.content.faqImage}
          title={t('faq.title')}
        />
      </PageSection>
    </main>
  );
};
